import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { ISDG } from "@api/schema"
import ErrorPage from "@components/ErrorPage"
import SpinnerPage from "@components/SpinnerPage"
import { DEFAULT_LOCALE, SDG_EXISTS_FOR_LANGUAGES, SDG_FALLBACK_LANGUAGE } from "@locales/locales-config"
import { newLoadCollectionAction } from "@redux/helper/actions"
import { IRequestState } from "@redux/helper/state"
import { AppState } from "@redux/reducer"
import { selectCollectionUsecaseState } from "@redux/reducer/data"
import { EntityType, ScopeTypes } from "@redux/reduxTypes"
import { useDynamicTranslation } from "@services/i18n"
import { BASE_URL } from "config"

export const PUBLIC_FOLDER_PATH = "public"
const SDG_FOLDER_PATH = "/assets/img/sdg/"

/**
 * Creates a valid path for the SDG-Images
 *
 * @param sdgId ID of the SDG
 * @param language the current locale to be used
 * @returns a valid path to the SDG-Image of the given SDG-ID
 */
export const sdgPathCreatorFunction = (sdgId: number, language: string): string => {

  if (!sdgId || sdgId < 1 || sdgId > 17 || !Number.isInteger(sdgId)) {
    return undefined
  }

  // #region file path structure
  const localePlaceholder = "[locale]"
  const sdgIdPlaceholder = "[id]"
  const pictureExtensionPlaceholder = "[extension]"
  // e.g. https://dev.api.projektfabrik.net/assets/img/sdg/en/sdg_icon_7.jpg
  const sdgImagePath = BASE_URL + SDG_FOLDER_PATH + localePlaceholder + "/sdg_icon_" + sdgIdPlaceholder + pictureExtensionPlaceholder
  // #endregion file path structure

  // #region constants
  const SDG_IMAGE_EXTENSION = ".jpg"
  // #endregion constants

  // get the given language or DEFAULT_LOCALE as fallback
  const currentLanguage = language || DEFAULT_LOCALE
  // choose the used language of the SDGs: use a fallback, if SDGs for the current language do not exist
  const sdgLanguage: string = SDG_EXISTS_FOR_LANGUAGES.includes(currentLanguage)
    ? currentLanguage
    : SDG_FALLBACK_LANGUAGE

  // replace all placeholders with values to get the full URL
  const sdgPath = sdgImagePath
    .replace(localePlaceholder, sdgLanguage)
    .replace(sdgIdPlaceholder, sdgId.toString())
    .replace(pictureExtensionPlaceholder, SDG_IMAGE_EXTENSION)

  return sdgPath
}


/**
 * structure of what the useSdgs hook returns
 */
interface IUseSdgsRequestData {
  sdgs: ISDG[]
  sdgRequest: IRequestState
  sdgErrorOrSpinnerPage: JSX.Element
}


/**
 * hook for using or fetching the SDGs
 *
 * @returns request, array of SDGs and potential error or spinner page
 */
export const useSdgs = (): IUseSdgsRequestData => {
  const dispatch = useDispatch()
  const t = useDynamicTranslation()

  const sdgRequest = useSelector((state: AppState) => selectCollectionUsecaseState(state, EntityType.Sdgs, ScopeTypes.SdgsLoading))

  const sdgs = sdgRequest.getItems<ISDG>()
  // const sdgs = useSelector((state: AppState) => state.platform.sdgs)

  useEffect(() => {
    if ((!sdgRequest || (!sdgRequest.loaded && !sdgRequest.loadingError)) && (!sdgRequest.isLoading)) {
      dispatch(newLoadCollectionAction(EntityType.Sdgs, {}, ScopeTypes.SdgsLoading))
    }
  }, [JSON.stringify(sdgRequest)])

  // #region error page creation
  let sdgErrorOrSpinnerPage: JSX.Element = null

  // order of the if-else-structure is important!
  // "hard failure" first, softer failure later, loading response latest
  if (sdgRequest?.loaded && !sdgs) {
    sdgErrorOrSpinnerPage = <ErrorPage statusCode={404} error={"failure.object.notFound"} />
  } else if (sdgRequest?.loadingError) {
    sdgErrorOrSpinnerPage = <ErrorPage statusCode={404} error={sdgRequest.loadingError} />
  } else if (sdgRequest && sdgRequest?.isLoading) {
    sdgErrorOrSpinnerPage = <SpinnerPage title={t("sdg", "sdgs")} description={t("common", "page.loading.title")} />
  }
  // #endregion error page creation

  return { sdgs, sdgRequest, sdgErrorOrSpinnerPage }
}


// #region query helper

/**
 * Queries for Sdg Elements in document using the SDG_FOLDER_PATH as identifier.
 *
 * @returns an array of sdg elements
 */
export const querySdgs = (): Element[] => {
  // find all images that have the SDG_FOLDER_PATH in their src attribute
  const sdgImages = Array.from(document.querySelectorAll("img"))
    .filter(img => img.hasAttribute("src") && img.getAttribute("src").indexOf(encodeURIComponent(SDG_FOLDER_PATH)) >= 0)

  // the img is two layers down from the sdgElement, so two parents up is the searched sdg element
  return sdgImages.map(e => e.parentElement.parentElement)
}

// #endregion query helper